import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import { Formik, Field, Form } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationModal from "../shared/ConfirmationModal";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";
import { Container, Row, Col, Card } from "react-bootstrap";

const OurTeamEdit: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [teamMember, setTeamMember] = useState<any>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [redirectAfterModal, setRedirectAfterModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");

  useEffect(() => {
    const fetchTeamMember = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}ourteam/ourteambyid/${id}`
        );
        if (response.data.length > 0) {
          setTeamMember(response.data[0]);
        } else {
          setModalMessage("Team member not found.");
          setShowModal(true);
        }
      } catch (error) {
        console.log(error);
        setFailureMessage("Error loading data. Please try again later.");
        setShowFailureModal(true);
      }
    };

    fetchTeamMember();
  }, [id]);

  const callApiUpdateTeamMember = async (values: any) => {
    const url = `${process.env.REACT_APP_API_URL}ourteam/updateteammate/${id}`;
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("designation", values.designation);
    formData.append("department", values.department);
    formData.append("description", values.description);
    formData.append("sequence", values.sequence);
    formData.append("linkedin", values.linkedin);
    formData.append("portfolio", values.portfolio);
    formData.append("twitter", values.twitter);

    if (selectedImage) {
      formData.append("image", selectedImage);
    } else if (teamMember?.image) {
      formData.append("image", teamMember.image);
    }

    try {
      const token = localStorage.getItem("jwttoken");
      if (!token) {
        setModalMessage("Unauthorized: Please login first.");
        setShowModal(true);
        return;
      }
      const response = await axios.put(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": token,
        },
      });

      if (response.status === 200) {
        setModalMessage("Are you sure you want to update this team member?");
        setRedirectAfterModal(true);
        setShowModal(true);
      }
    } catch (error) {
      console.log(error);
      setFailureMessage("Failed to update team member. Please try again.");
      setShowFailureModal(true);
    }
  };

  const handleSubmit = async (values: any) => {
    const hasChanges =
      JSON.stringify(values) !==
      JSON.stringify({
        name: teamMember.name || "",
        designation: teamMember.designation || "",
        department: teamMember.department || "",
        description: teamMember.description || "",
        sequence: teamMember.sequence || "",
        linkedin: teamMember.linkedin || "",
        portfolio: teamMember.portfolio || "",
        twitter: teamMember.twitter || "",
      });

    if (!hasChanges && !selectedImage) {
      setModalMessage("No changes were made.");
      setShowModal(true);
      return;
    }

    await callApiUpdateTeamMember(values);
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (redirectAfterModal) {
      setSuccessMessage("Team member updated successfully!");
      setShowSuccessModal(true);

      setTimeout(() => {
        navigate("/ourteam");
      }, 3000);
    }
  };

  if (!teamMember && !modalMessage) {
    return <div>Loading...</div>;
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Edit Team Member</h4>
            </Card.Header>
            <Card.Body>
              <Formik
                initialValues={{
                  name: teamMember.name || "",
                  designation: teamMember.designation || "",
                  department: teamMember.department || "",
                  description: teamMember.description || "",
                  sequence: teamMember.sequence || "",
                  linkedin: teamMember.linkedin || "",
                  portfolio: teamMember.portfolio || "",
                  twitter: teamMember.twitter || "",
                }}
                onSubmit={async (values, { resetForm }) => {
                  await handleSubmit(values);
                  resetForm();
                }}
                enableReinitialize
              >
                <Form>
                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="name" className="form-label">
                        Name:
                      </label>
                      <Field
                        name="name"
                        type="text"
                        className="form-control"
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="designation" className="form-label">
                        Designation:
                      </label>
                      <Field
                        name="designation"
                        type="text"
                        className="form-control"
                        required
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="department" className="form-label">
                        Department:
                      </label>
                      <Field
                        name="department"
                        type="text"
                        className="form-control"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="description" className="form-label">
                        Description:
                      </label>
                      <Field
                        name="description"
                        as="textarea"
                        className="form-control"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="sequence" className="form-label">
                        Sequence:
                      </label>
                      <Field
                        name="sequence"
                        type="number"
                        className="form-control"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="linkedin" className="form-label">
                        Linkedin:
                      </label>
                      <Field
                        name="linkedin"
                        type="text"
                        className="form-control"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="portfolio" className="form-label">
                        Portfolio:
                      </label>
                      <Field
                        name="portfolio"
                        type="text"
                        className="form-control"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="twitter" className="form-label">
                        Twitter:
                      </label>
                      <Field
                        name="twitter"
                        type="text"
                        className="form-control"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <label htmlFor="image" className="form-label">
                        Upload New Image (Optional):
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setSelectedImage(e.target.files[0]);
                          }
                        }}
                      />
                      {teamMember.image && !selectedImage && (
                        <small>Current Image: {teamMember.image}</small>
                      )}
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col className="text-center">
                      <button type="submit" className="btn btn-success me-2">
                        Update
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger ms-2"
                        onClick={() => navigate("/ourteam")}
                      >
                        Go Back
                      </button>
                    </Col>
                  </Row>
                  <div
                    className="col-12 overflow-auto p-4"
                    style={{ maxHeight: "calc(100vh - 200px)" }}
                  ></div>
                </Form>
              </Formik>

              <ConfirmationModal
                show={showModal}
                onHide={handleModalClose}
                modalMessage={modalMessage}
                operationType="Confirm"
              />
              <NewconfirmationModal
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
                modalMessage={successMessage}
                operationType="Ok"
                timeoutDuration={3000}
              />

              <AllFailureModal
                show={showFailureModal}
                onHide={() => setShowFailureModal(false)}
                modalMessage={failureMessage}
                operationType="Ok"
                timeoutDuration={5000}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OurTeamEdit;
